/**
 * useAuth Hook - 管理全局登录状态（接入 shouqian 统一登录 / 父域 Cookie SSO）
 *
 * 无鉴权 / 鉴权过期时统一跳转父系统首页（shouqian），不再弹独立登录框。
 */
import { useState, useEffect, useCallback } from 'react';
import { authApi } from '../services/api';

interface User {
  id: number;
  username: string;
  display_name: string;
  role: string;
}

// 父系统（shouqian）首页，未登录/过期时跳转；可通过 env 覆盖
const SSO_HOME_URL = process.env.REACT_APP_SSO_HOME_URL || 'https://www.healthsafety.cn';

// 当前站点是否处于 SSO 父域之下（只有 *.healthsafety.cn 才能读到父域 SSO Cookie）。
// 测试环境（IP / localhost / 其他域名）SSO Cookie 不存在，改用本地账号登录页。
const isSsoDomain = (() => {
  try {
    const ssoHost = new URL(SSO_HOME_URL).hostname;      // 如 www.healthsafety.cn
    const parentDomain = ssoHost.split('.').slice(-2).join('.'); // healthsafety.cn
    const host = window.location.hostname;
    return host === parentDomain || host.endsWith('.' + parentDomain);
  } catch {
    return false;
  }
})();

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // 未登录/鉴权过期时的去向：SSO 域名 → 父系统首页；否则 → 本地登录页
  const redirectToSsoHome = useCallback(() => {
    if (isSsoDomain) {
      window.location.href = SSO_HOME_URL;
    } else if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, []);

  const clearLocalAuth = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  // 初始化：从 localStorage 加载用户信息；若无，则尝试从统一登录 Cookie 恢复（调 /me）
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        setLoading(false);
        return;
      } catch (e) {
        console.error('Failed to parse user from localStorage:', e);
      }
    }
    // 统一登录：无本地用户时，用 Cookie（同源自动携带）静默调 /me 恢复登录态。
    // 用 fetch 绕过 axios 拦截器，避免未登录时触发 401 事件。
    const base = process.env.REACT_APP_API_URL ?? '';
    fetch(`${base}/api/auth/me`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('未登录'))))
      .then((u) => {
        if (u && u.id) {
          localStorage.setItem('user', JSON.stringify(u));
          setUser(u);
        } else {
          // Cookie 无效或未登录，跳父系统首页登录
          redirectToSsoHome();
        }
      })
      .catch(() => {
        // 未登录 / 网络异常：清本地并跳父系统首页
        clearLocalAuth();
        redirectToSsoHome();
      })
      .finally(() => setLoading(false));
  }, [redirectToSsoHome, clearLocalAuth]);

  // 监听 401 未授权事件（鉴权过期）→ 清本地并跳父系统首页
  useEffect(() => {
    const handleUnauthorized = () => {
      clearLocalAuth();
      redirectToSsoHome();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [redirectToSsoHome, clearLocalAuth]);

  // 监听用户信息更新事件
  useEffect(() => {
    const handleUserUpdated = () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
          console.log('🔄 用户信息已更新');
        } catch (e) {
          console.error('Failed to parse updated user from localStorage:', e);
        }
      }
    };

    window.addEventListener('user:updated', handleUserUpdated);
    return () => window.removeEventListener('user:updated', handleUserUpdated);
  }, []);

  // 登出：清本地后跳父系统首页（由 shouqian 统一退出）
  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearLocalAuth();
      redirectToSsoHome();
    }
  };

  // 检查是否已登录
  const isAuthenticated = !!user;

  return {
    user,
    loading,
    isAuthenticated,
    logout,
  };
};
